import {
    Alert,
    Box,
    Button,
    ButtonProps,
    CircularProgress,
    Grid,
    Snackbar,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { setMortgageAction, setNewMortgageAction } from "../../../contexts/mortgage/Actions";
import { useMortgageDispatch, useMortgageState } from "../../../contexts/mortgage/Reducer";
import { setStepAction } from "../../../contexts/steps/Actions";
import { useStepDispatch } from "../../../contexts/steps/Reducer";
import { LoadingState } from "../../../model/LoadingState";
import { ConfiguredOffer } from "../../../model/MortgageExtensionModel";
import { AnalyticsActivity, AppComponent } from "../../../services/event-service/EventService";
import { useEventService } from "../../../services/event-service/EventServiceContext";
import { useMortgageClient } from "../../../services/mortgage-client/MortgageClientContext";
import AddressInfo from "../../address-info/AddressInfo";
import ErrorMessage from "../../feedback/ErrorMessage";
import MortgageStepper from "../../mortgage-stepper/MortgageStepper";
import ConfirmationDialog from "../popups/ConfirmationDialog";
import MortgageGeneralProvisions from "./MortgageGeneralProvisions";
import NewMortgageSummary from "./NewMortgageSummary";
import SwipeButton from "./SwipeButton";
import WholeLifeInsurance from "./WholeLifeInsurance";

export const SummarySubHeaderLabel = styled(Typography)(({ theme }) => ({
    color: "#333333",
    fontSize: "18px",
    fontWeight: "500",
    letterSpacing: 0,
    lineHeight: "30px",
    marginBottom: "24px",
    marginTop: "8px",
    [theme.breakpoints.up("lg")]: {
        marginTop: "16px",
    },
}));

export const SectionDivider = styled(Box)(({ theme }) => ({
    height: "48px",
    [theme.breakpoints.up("lg")]: {
        height: "64px",
    },
}));

export const ButtonBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flex: 1,
    marginTop: "38px",
    marginBottom: "48px",
    flexDirection: "column-reverse",
    [theme.breakpoints.up("md")]: {
        marginTop: "48px",
        flexDirection: "row",
    },
}));

export const BackButton = styled(Button)<ButtonProps>(({ theme }) => ({
    color: "#0093D1",
    fontSize: "16px",
    fontWeight: 500,
    textTransform: "none",
    ariaHasPopup: "dialog",
}));

export const Error = styled(Snackbar)<ButtonProps>(({ theme }) => ({
    width: "80%",
    [theme.breakpoints.up("md")]: {
        width: "50%",
    },
    [theme.breakpoints.up("lg")]: {
        width: "unset",
    },
}));

export const ForwardButton = styled(Button)<ButtonProps>(({ theme }) => ({
    fontSize: "16px",
    fontWeight: 500,
    backgroundColor: "#0093D1",
    borderColor: "#0093D1",
    borderRadius: "4px",
    boxShadow: "none",
    textTransform: "none",
    marginBottom: "16px",
    "&:hover": {
        backgroundColor: "#0093D1",
        borderColor: "#0093D1",
        boxShadow: "none",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#0093D1",
        borderColor: "#0093D1",
    },
    width: "100%",
    [theme.breakpoints.up("md")]: {
        minWidth: "280px",
        height: "64px",
        width: "unset",
    },
    [theme.breakpoints.up("lg")]: {
        marginBottom: "0px",
        height: "inherit",
        minWidth: "unset",
        width: "unset",
    },
    ariaHasPopup: "dialog",
}));

const StepperWrapper = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up("lg")]: {
        marginBottom: "48px",
    },
}));

/**
 * A second screen of the mortgage extension wizard which allows the customer to view the summary of his configured mortgage that he wants to extend.
 */
const SummaryScreen = () => {
    const { t } = useTranslation();

    const mortgageClient = useMortgageClient();
    const stepDispatch = useStepDispatch();

    const { mortgage, newMortgage } = useMortgageState();
    const mortgageDispatch = useMortgageDispatch();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.Success);
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorAlert, setErrorAlert] = useState("init");
    const [respondedToOffer, setRespondedToOffer] = useState(false);
    const eventService = useEventService();
    const theme = useTheme();
    const matchesWebView = useMediaQuery(theme.breakpoints.up("md"));

    if (mortgage === undefined) {
        return <ErrorMessage />;
    }

    const disableGoForward = () => {
        return mortgage.showInsuranceDeath && !respondedToOffer;
    };

    const mortgageExtensionConfirmed = async () => {
        if (matchesWebView) {
            setConfirmDialogOpen(false);
        }
        if (!newMortgage) {
            console.error("Something went wrong! New mortgage undefined!");
            return;
        }
        try {
            eventService.sendAnalyticsEvent(AnalyticsActivity.EXTEND_MORTGAGE_CONFIRMED, AppComponent.SUMMARY_SCREEN);
            setLoadingState(LoadingState.Loading);

            const offerConfirmation: ConfiguredOffer = {
                id: newMortgage.id,
                duration: newMortgage.duration,
                amount: newMortgage.amount,
                repayment: newMortgage.repayment,
                interestRate: newMortgage.interestRate,
                lifeInsurance: newMortgage.includeLifeInsuranceOffer === true,
            };

            await mortgageClient.submitOffer(offerConfirmation);

            stepDispatch(setStepAction(3));
            setLoadingState(LoadingState.Success);
        } catch (error: any) {
            setErrorAlert(error.message);
            setErrorAlertOpen(true);
            setLoadingState(LoadingState.Failed);
        }
    };

    const handleErrorAlertClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setErrorAlertOpen(false);
    };

    const cancelMortgageExtension = () => {
        eventService.sendAnalyticsEvent(AnalyticsActivity.LEAVE_PROCESS, AppComponent.SUMMARY_SCREEN);

        stepDispatch(setStepAction(0));
        mortgageDispatch(setNewMortgageAction(undefined));
        mortgageDispatch(setMortgageAction(undefined));
    };

    return (
        <Grid container data-cy={"summary-screen"}>
            <Grid item sm={12} md={12} lg={12}>
                <StepperWrapper>
                    <MortgageStepper />
                </StepperWrapper>
            </Grid>
            <Grid item sm={0} md={1} lg={1}></Grid>
            <Grid item sm={12} md={10} lg={10}>
                <AddressInfo selectedMortgage={mortgage} />
                <Box display={"flex"} alignItems="center" justifyContent="flex-start" data-cy={"new-mortgage-text"}>
                    <SummarySubHeaderLabel>{t("SummaryScreen.Your new mortgage")}</SummarySubHeaderLabel>
                </Box>
                <NewMortgageSummary />
                <SectionDivider />
                <Box display={"flex"} alignItems="center" justifyContent="flex-start" data-cy={"general-terms-text"}>
                    <SummarySubHeaderLabel>{t("General.General terms")}</SummarySubHeaderLabel>
                </Box>
                <MortgageGeneralProvisions />
                {mortgage.showInsuranceDeath && (
                    <WholeLifeInsurance loadingState={loadingState} setRespondedToOffer={setRespondedToOffer} />
                )}
                <ButtonBox>
                    <BackButton
                        onClick={() => setCancelDialogOpen(true)}
                        disabled={loadingState === LoadingState.Loading}
                        data-cy={"back-button"}>
                        {t("Actions.Cancel")}
                    </BackButton>

                    {matchesWebView ? (
                        <Box sx={{ position: "relative", marginBottom: "24px" }}>
                            <ForwardButton
                                variant="contained"
                                onClick={() => setConfirmDialogOpen(true)}
                                disabled={disableGoForward()}
                                data-cy={"forward-button"}>
                                {t("SummaryScreen.Extend irrevocably mortgage")}
                            </ForwardButton>

                            {loadingState === LoadingState.Loading && (
                                <CircularProgress
                                    data-testid={"loading"}
                                    size={24}
                                    sx={{
                                        position: "absolute",
                                        left: "50%",
                                        top: "50%",
                                        marginTop: "-12px",
                                        marginLeft: "-12px",
                                    }}
                                />
                            )}
                        </Box>
                    ) : (
                        <SwipeButton
                            confirmAction={mortgageExtensionConfirmed}
                            disableGoForward={disableGoForward()}
                            loadingState={loadingState}
                        />
                    )}
                </ButtonBox>
            </Grid>
            <Grid item sm={0} md={1} lg={1}></Grid>

            <ConfirmationDialog
                open={confirmDialogOpen}
                title={"Actions.Extend mortgage"}
                message={"SummaryScreen.Do you want to extend your mortgage? Please note that this is binding."}
                confirmButtonLabel={"Actions.Extend"}
                cancelButtonLabel={"Actions.Cancel"}
                cancelAction={() => setConfirmDialogOpen(false)}
                confirmAction={mortgageExtensionConfirmed}
            />

            <ConfirmationDialog
                open={cancelDialogOpen}
                title={"Actions.Leave the process?"}
                message={"Actions.Your entries will be lost"}
                confirmButtonLabel={"Actions.Leave process"}
                cancelButtonLabel={"Actions.Back"}
                cancelAction={() => setCancelDialogOpen(false)}
                confirmAction={cancelMortgageExtension}
            />

            <Error
                open={errorAlertOpen}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                onClose={handleErrorAlertClose}>
                <Alert severity="error" elevation={6} sx={{ width: "100%" }}>
                    {t(errorAlert)}
                </Alert>
            </Error>
        </Grid>
    );
};

export default SummaryScreen;
